/**
 * vp-pdp.js — Vale Pet Foods Product Detail Page JS
 * Handles: gallery, size selector, purchase type, qty, ATC, sticky bar, FAQ, reveal
 */

window.VpPdp = {

  /**
   * Initialise a PDP section
   * @param {string} sectionId — Shopify section.id
   * @param {object} config    — { productHandle, currentVariantId, variantsData, currencySymbol, moneyFormat }
   */
  init(sectionId, config) {
    const s = sectionId;
    this._state = this._state || {};
    this._state[s] = {
      variantId: config.currentVariantId,
      qty: 1,
      purchaseType: 'once', // 'once' | selling_plan_id
      variants: config.variantsData || [],
      currencySymbol: config.currencySymbol || '£',
      moneyFormat: config.moneyFormat || '£{{amount}}'
    };

    this._initStickyGallery(s);
    this._initGallery(s);
    this._initSizeSelector(s);
    this._initPurchaseOptions(s);
    this._initQty(s);
    this._initStickyBar(s);
    this._initFaq(s);
    this._initReveal();
    this._updatePriceDisplay(s);
    this._syncStickyBtn(s);
  },

  // ─── Helpers ─────────────────────────────────────────────────────────────

  _money(cents, symbol) {
    symbol = symbol || '£';
    return symbol + (cents / 100).toFixed(2);
  },

  _getState(sectionId) {
    return (this._state || {})[sectionId] || {};
  },

  _currentVariant(sectionId) {
    const st = this._getState(sectionId);
    return st.variants.find(v => v.id === st.variantId) || st.variants[0];
  },

  // ─── Sticky gallery top offset ────────────────────────────────────────────
  // Measures the actual rendered header height from the DOM and writes
  // --pdp-sticky-top to :root so the CSS sticky top is always accurate,
  // even when the Dawn header changes size (mobile menu open, etc.).

  _initStickyGallery(s) {
    const gallery = document.getElementById(`pdpGallery-${s}`);
    if (!gallery) return;

    // ── Fix all known blockers of position:sticky ──
    // 1) body.gradient has background-attachment:fixed which prevents sticky
    document.body.classList.add('pdp-sticky-fix');
    document.body.style.backgroundAttachment = 'scroll';

    // 2) Walk every ancestor from gallery to <html>, ensure none
    //    have overflow hidden/auto/scroll or a transform/will-change
    let el = gallery.parentElement;
    while (el && el !== document.documentElement) {
      const cs = getComputedStyle(el);
      const ov = cs.overflow + cs.overflowX + cs.overflowY;
      if (/hidden|auto|scroll/.test(ov) && el.tagName !== 'HTML') {
        el.style.overflow = 'visible';
      }
      if (cs.transform && cs.transform !== 'none') {
        el.style.transform = 'none';
      }
      if (cs.willChange && cs.willChange !== 'auto') {
        el.style.willChange = 'auto';
      }
      el = el.parentElement;
    }

    // 3) Set --pdp-sticky-top from actual rendered header height
    const PADDING = 24; // ~--space-lg

    function measure() {
      const headerEl =
        document.querySelector('.shopify-section-group-header-group') ||
        document.querySelector('.section-header') ||
        document.querySelector('header[role="banner"]') ||
        document.querySelector('header');
      const h = headerEl ? headerEl.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty('--pdp-sticky-top', `${Math.round(h + PADDING)}px`);
    }

    measure();
    window.addEventListener('load', measure, { once: true });

    const headerEl =
      document.querySelector('.shopify-section-group-header-group') ||
      document.querySelector('.section-header');
    if (headerEl && typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(measure).observe(headerEl);
    }
  },

  // ─── Gallery ─────────────────────────────────────────────────────────────

  _initGallery(s) {
    const thumbs = document.querySelectorAll(`.pdp-thumb[data-gallery-id="${s}"]`);
    const mainImg = document.getElementById(`pdpMainImage-${s}`);
    if (!mainImg) return;

    thumbs.forEach(btn => {
      btn.addEventListener('click', () => {
        thumbs.forEach(t => t.classList.remove('active'));
        btn.classList.add('active');
        mainImg.style.opacity = '0';
        mainImg.style.transform = 'scale(0.97)';
        setTimeout(() => {
          mainImg.src = btn.dataset.full;
          mainImg.style.opacity = '1';
          mainImg.style.transform = 'scale(1)';
        }, 180);
      });
    });
  },

  // ─── Size / Variant selector ─────────────────────────────────────────────

  _initSizeSelector(s) {
    const sizeBtns = document.querySelectorAll(`.pdp-size-btn[data-section="${s}"]`);
    if (!sizeBtns.length) return;

    sizeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        // Deselect siblings in same option group
        const optIdx = btn.dataset.optionIndex;
        document.querySelectorAll(`.pdp-size-btn[data-section="${s}"][data-option-index="${optIdx}"]`)
          .forEach(b => b.classList.remove('pdp-size-selected'));
        btn.classList.add('pdp-size-selected');

        // Find matching variant
        const selectedValues = [];
        document.querySelectorAll(`[data-option-index]`).forEach(grid => {
          const selected = grid.querySelector('.pdp-size-selected');
          if (selected) selectedValues[parseInt(grid.dataset.optionIndex)] = selected.dataset.size;
        });

        const st = this._getState(s);
        const matchedVariant = st.variants.find(v => {
          return selectedValues.every((val, idx) => {
            const keys = ['option1', 'option2', 'option3'];
            return !val || v[keys[idx]] === val;
          });
        });

        if (matchedVariant) {
          st.variantId = matchedVariant.id;
          // Update hidden input for form submission
          const input = document.getElementById(`pdpVariantInput-${s}`);
          if (input) input.value = matchedVariant.id;
        }

        this._updatePriceDisplay(s);
        this._syncStickyBtn(s);
      });
    });
  },

  // ─── Purchase type (one-time vs subscribe) — two-stage swatch ─────────────

  _initPurchaseOptions(s) {
    const container = document.getElementById(`pdpPurchaseOpts-${s}`);
    if (!container) return;

    const freqPicker = document.getElementById(`pdpFreqPicker-${s}`);
    const planInput  = document.getElementById(`pdpSellingPlan-${s}`);

    // Helper: get discount from the active freq button
    const getActiveFreqDiscount = () => {
      if (!freqPicker) return { type: 'percentage', value: 15 };
      const active = freqPicker.querySelector('.pdp-freq-selected');
      if (!active) return { type: 'percentage', value: 15 };
      return {
        type:  active.dataset.discountType  || 'percentage',
        value: parseFloat(active.dataset.discountValue) || 0
      };
    };

    // ── Top-level purchase swatches (One-Time / Subscribe & Save) ──
    const swatches = container.querySelectorAll('.pdp-purchase-swatch');
    swatches.forEach(btn => {
      btn.addEventListener('click', () => {
        const st   = this._getState(s);
        const type = btn.dataset.purchaseType;

        swatches.forEach(sw => sw.classList.remove('pps-active'));
        btn.classList.add('pps-active');

        if (type === 'once') {
          st.purchaseType      = 'once';
          st._discountType     = null;
          st._discountValue    = 0;
          if (planInput) planInput.value = '';
          if (freqPicker) freqPicker.style.display = 'none';
        } else {
          // Subscribe & Save — show the frequency picker
          if (freqPicker) {
            freqPicker.style.display = 'block';
            // Pick the currently selected freq plan
            const active = freqPicker.querySelector('.pdp-freq-selected');
            if (active) {
              st.purchaseType   = active.dataset.planId;
              const disc        = getActiveFreqDiscount();
              st._discountType  = disc.type;
              st._discountValue = disc.value;
              if (planInput) planInput.value = active.dataset.planId;
            }
          }
        }

        this._updatePriceDisplay(s);
        this._syncStickyBtn(s);
      });
    });

    // ── Frequency buttons ──
    if (freqPicker) {
      freqPicker.querySelectorAll('.pdp-freq-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          freqPicker.querySelectorAll('.pdp-freq-btn').forEach(b => b.classList.remove('pdp-freq-selected'));
          btn.classList.add('pdp-freq-selected');

          const st          = this._getState(s);
          st.purchaseType   = btn.dataset.planId;
          const disc        = getActiveFreqDiscount();
          st._discountType  = disc.type;
          st._discountValue = disc.value;
          if (planInput) planInput.value = btn.dataset.planId;

          this._updatePriceDisplay(s);
          this._syncStickyBtn(s);
        });
      });

      // Pre-wire first freq button's plan id into state if sub is active on load
      const firstFreq = freqPicker.querySelector('.pdp-freq-btn');
      if (firstFreq) {
        const st          = this._getState(s);
        // Store first plan details so Subscribe price shows correctly on load
        st._firstPlanId         = firstFreq.dataset.planId;
        st._firstDiscountType   = firstFreq.dataset.discountType  || 'percentage';
        st._firstDiscountValue  = parseFloat(firstFreq.dataset.discountValue) || 0;
      }
    }
  },

  // ─── Quantity stepper ────────────────────────────────────────────────────

  _initQty(s) {
    const input = document.getElementById(`pdpQtyInput-${s}`);
    if (!input) return;

    document.querySelectorAll(`[data-action][data-section="${s}"]`).forEach(btn => {
      btn.addEventListener('click', () => {
        const delta = btn.dataset.action === 'increase' ? 1 : -1;
        let val = parseInt(input.value || 1) + delta;
        val = Math.max(1, Math.min(99, val));
        input.value = val;
        this._getState(s).qty = val;
        this._updatePriceDisplay(s);
        this._syncStickyBtn(s);
      });
    });

    input.addEventListener('change', () => {
      let val = parseInt(input.value) || 1;
      val = Math.max(1, Math.min(99, val));
      input.value = val;
      this._getState(s).qty = val;
      this._updatePriceDisplay(s);
      this._syncStickyBtn(s);
    });
  },

  // ─── Price display ────────────────────────────────────────────────────────

  _updatePriceDisplay(s) {
    const st = this._getState(s);
    const variant = this._currentVariant(s);
    if (!variant) return;

    const sym = st.currencySymbol || '£';
    const isSub = st.purchaseType !== 'once';
    // For subscribe, apply the selected plan's discount
    let price = variant.price; // in cents
    let comparePrice = variant.compare_at_price;

    if (isSub) {
      const discType = st._discountType || 'percentage';
      const discVal = st._discountValue || 0;
      if (discVal > 0) {
        if (discType === 'percentage') {
          price = Math.round(price * (1 - discVal / 100));
        } else {
          price = Math.max(0, price - discVal);
        }
      }
      comparePrice = variant.price;
    }

    const totalCents = price * (st.qty || 1);

    // Price display
    const priceEl = document.getElementById(`pdpPrice-${s}`);
    if (priceEl) priceEl.textContent = this._money(price, sym);

    const compareEl = document.getElementById(`pdpCompare-${s}`);
    if (compareEl) {
      if (comparePrice && comparePrice > price) {
        compareEl.textContent = this._money(comparePrice, sym);
        compareEl.style.display = 'inline';
      } else {
        compareEl.style.display = 'none';
      }
    }

    const saveEl = document.getElementById(`pdpSave-${s}`);
    if (saveEl) {
      if (comparePrice && comparePrice > price) {
        const pct = Math.round((1 - price / comparePrice) * 100);
        saveEl.textContent = `Save ${pct}%`;
      } else {
        saveEl.style.display = 'none';
      }
    }

    // ATC button total
    const totalEl = document.getElementById(`pdpTotal-${s}`);
    if (totalEl) totalEl.textContent = this._money(totalCents, sym);

    // Purchase card prices
    const oncePriceEl = document.getElementById(`pdpOncePrice-${s}`);
    if (oncePriceEl) oncePriceEl.textContent = this._money(variant.price, sym);

    // Subscribe price — read discount from the active frequency button
    const subPriceEl = document.getElementById(`pdpSubPrice-${s}`);
    if (subPriceEl) {
      const freqPicker = document.getElementById(`pdpFreqPicker-${s}`);
      const activeFreq = freqPicker && freqPicker.querySelector('.pdp-freq-selected');
      let subPrice = variant.price; // same as one-time unless plan has discount
      if (activeFreq) {
        const dType = activeFreq.dataset.discountType || 'percentage';
        const dVal  = parseFloat(activeFreq.dataset.discountValue) || 0;
        if (dVal > 0) {
          subPrice = dType === 'percentage'
            ? Math.round(variant.price * (1 - dVal / 100))
            : Math.max(0, variant.price - dVal);
        }
      }
      subPriceEl.textContent = this._money(subPrice, sym);
    }

    // One-time swatch price
    const swatchContainer = document.getElementById(`pdpPurchaseOpts-${s}`);
    if (swatchContainer) {
      const onceSwatch = swatchContainer.querySelector('[data-purchase-type="once"] .pps-price');
      if (onceSwatch) onceSwatch.textContent = this._money(variant.price, sym);
    }
  },

  // ─── Sticky bar sync ─────────────────────────────────────────────────────

  _syncStickyBtn(s) {
    const st = this._getState(s);
    const variant = this._currentVariant(s);
    if (!variant) return;

    const sym = st.currencySymbol || '£';
    const isSub = st.purchaseType !== 'once';
    let price = variant.price;
    if (isSub) {
      const discType = st._discountType || 'percentage';
      const discVal = st._discountValue || 0;
      if (discVal > 0) {
        if (discType === 'percentage') {
          price = Math.round(price * (1 - discVal / 100));
        } else {
          price = Math.max(0, price - discVal);
        }
      }
    }
    const total = price * (st.qty || 1);

    const stickyPrice = document.getElementById(`pdpStickyPrice-${s}`);
    if (stickyPrice) stickyPrice.textContent = this._money(total, sym);

    const stickySize = document.getElementById(`pdpStickySize-${s}`);
    if (stickySize) stickySize.textContent = variant.title.replace(' / ', ' · ');
  },

  // ─── Sticky bar visibility ───────────────────────────────────────────────

  _initStickyBar(s) {
    const bar = document.getElementById(`pdpStickyBar-${s}`);
    const atcRow = document.querySelector(`#VpPdpHero-${s} .pdp-atc-row`);
    if (!bar || !atcRow) return;

    const obs = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        bar.classList.toggle('pdp-sticky-visible', !entry.isIntersecting);
      });
    }, { threshold: 0.1 });
    obs.observe(atcRow);

    // Sticky btn delegates to main ATC form
    const stickyBtn = bar.querySelector('.pdp-sticky-btn');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const mainBtn = document.getElementById(`pdpAtcBtn-${s}`);
        if (mainBtn) mainBtn.click();
      });
    }
  },

  // ─── FAQ accordion ───────────────────────────────────────────────────────

  _initFaq(s) {
    const section = document.getElementById(`VpPdpFaq-${s}`);
    if (!section) return;

    section.querySelectorAll('[data-faq-toggle]').forEach(btn => {
      const item = btn.closest('.pdp-faq-item');
      const answer = item.querySelector('.pdp-faq-a');
      if (!answer) return;

      // Set initial max-height
      if (item.classList.contains('open')) {
        answer.style.maxHeight = answer.scrollHeight + 'px';
      } else {
        answer.style.maxHeight = '0';
      }

      btn.addEventListener('click', () => {
        const isOpen = item.classList.contains('open');
        // Close all
        section.querySelectorAll('.pdp-faq-item.open').forEach(openItem => {
          openItem.classList.remove('open');
          const a = openItem.querySelector('.pdp-faq-a');
          if (a) a.style.maxHeight = '0';
        });
        // Open this one if it was closed
        if (!isOpen) {
          item.classList.add('open');
          answer.style.maxHeight = answer.scrollHeight + 'px';
        }
      });
    });
  },

  // ─── Scroll reveal ───────────────────────────────────────────────────────

  _initReveal() {
    if (this._revealInit) return;
    this._revealInit = true;

    const items = document.querySelectorAll('.reveal:not(.visible)');
    if (!items.length) return;

    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const siblings = Array.from(entry.target.parentElement.querySelectorAll('.reveal'));
          const idx = siblings.indexOf(entry.target);
          entry.target.style.transitionDelay = (idx * 0.08) + 's';
          entry.target.classList.add('visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    items.forEach(el => obs.observe(el));
  }

};
